import React, { useState } from 'react';
import { CartItem, targetAppStore } from '../store';
import { CreditCard, Tag, AlertTriangle, CheckCircle2, ShieldCheck } from 'lucide-react';

interface CheckoutFormProps {
  cart: CartItem[];
  appliedDiscounts: string[];
  isBug1Active: boolean;
  isBug2Active: boolean;
  isPatched: boolean;
}

export const CheckoutForm: React.FC<CheckoutFormProps> = ({
  cart,
  appliedDiscounts,
  isBug1Active,
  isBug2Active,
  isPatched,
}) => {
  const [promoInput, setPromoInput] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);

  // Subtotal calculation
  const subtotal = cart.reduce((acc, item) => acc + item.product.price * item.quantity, 0);

  // DISCOUNT CALCULATION LOGIC:
  // Evaluates according to whether Bug #1 or Bug #2 is active vs autopatched
  const couponRate = appliedDiscounts.length > 0 ? 30 : 0; // Default 30% if discount applied

  const calculateDiscount = (): number => {
    if (appliedDiscounts.length === 0) return 0;

    // --- BUG 1: Zero-price division resulting in NaN ---
    if (isBug1Active && !isPatched) {
      // ❌ BUGGY CODE: Evaluates (couponRate / item.price) on free item
      let totalDiscount = 0;
      for (const item of cart) {
        const itemPrice = item.product.price;
        const multiplier = itemPrice === 0 ? (couponRate / itemPrice) : (couponRate / 100);
        const itemDiscount = itemPrice * multiplier; // 0 * (30/0) -> 0 * Infinity = NaN!
        totalDiscount += itemDiscount * item.quantity;
      }
      return totalDiscount;
    }

    // --- BUG 2: Unclamped discount stacking ---
    if (isBug2Active && !isPatched) {
      // ❌ BUGGY CODE: Sums all promo codes without ceiling
      const RATES: Record<string, number> = {
        WELCOME20: 20,
        FLASH50: 50,
        VIP40: 40,
        SUMMER30: 30,
      };
      const totalPct = appliedDiscounts.reduce((sum, code) => sum + (RATES[code] || 25), 0);
      return subtotal * (totalPct / 100); // Can exceed 100% yielding negative total!
    }

    // --- ✅ AUTOPATCHED / SAFE CODE ---
    let safeDiscount = 0;
    const RATES: Record<string, number> = {
      WELCOME20: 20,
      FLASH50: 50,
      VIP40: 40,
      SUMMER30: 30,
    };
    const totalPct = Math.min(
      70, // Capped at max 70% discount
      appliedDiscounts.reduce((sum, code) => sum + (RATES[code] || 20), 0)
    );

    for (const item of cart) {
      if (item.product.price <= 0) continue; // Skip free promo items safely
      const itemDiscount = (item.product.price * (totalPct / 100)) * item.quantity;
      safeDiscount += itemDiscount;
    }

    return Math.round(safeDiscount * 100) / 100;
  };

  const discountAmount = calculateDiscount();
  const rawTotal = subtotal - discountAmount;
  const isNanDetected = Number.isNaN(rawTotal);
  const isNegativeDetected = rawTotal < 0;

  const displayTotal = isNanDetected
    ? 'NaN'
    : isNegativeDetected
    ? `-$${Math.abs(rawTotal).toFixed(2)}`
    : `$${rawTotal.toFixed(2)}`;

  const handleApplyPromo = (e: React.FormEvent) => {
    e.preventDefault();
    if (promoInput.trim()) {
      targetAppStore.applyDiscount(promoInput.trim().toUpperCase());
      setPromoInput('');
    }
  };

  const handleCheckout = () => {
    if (isNanDetected) {
      const err = new Error(
        `[PaymentGatewayError] Invalid Transaction Amount: received 'NaN' for customer checkout total. Stripe tokenization rejected.`
      );
      targetAppStore.setLastError(err.message);
      // Dispatch custom incident event for auto-detection
      window.dispatchEvent(
        new CustomEvent('autofix-crash-detected', {
          detail: {
            bugId: 'bug-1',
            error: err,
            action: 'Checkout button clicked with NaN total',
          },
        })
      );
      return;
    }

    if (isNegativeDetected) {
      const err = new Error(
        `[FraudDetectionAlert] Exploit Detected: Cart total calculated to negative value ($${rawTotal.toFixed(2)}). Merchant payout prevented.`
      );
      targetAppStore.setLastError(err.message);
      window.dispatchEvent(
        new CustomEvent('autofix-crash-detected', {
          detail: {
            bugId: 'bug-2',
            error: err,
            action: 'Checkout attempted with negative total amount',
          },
        })
      );
      return;
    }

    setIsProcessing(true);
    setTimeout(() => {
      setIsProcessing(false);
      setPaymentSuccess(true);
      setTimeout(() => setPaymentSuccess(false), 4000);
    }, 800);
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        background: 'var(--bg-card)',
        padding: 14,
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--border)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h4 style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-1)', display: 'flex', alignItems: 'center', gap: 6 }}>
          <CreditCard size={14} style={{ color: 'var(--accent)' }} /> Checkout & Payment Gateway
        </h4>
        <span style={{ fontSize: 10, color: 'var(--text-3)', display: 'flex', alignItems: 'center', gap: 3 }}>
          <ShieldCheck size={11} style={{ color: 'var(--ok)' }} /> PCI-DSS Encrypted
        </span>
      </div>

      {/* Promo Code Entry */}
      <form onSubmit={handleApplyPromo} style={{ display: 'flex', gap: 6 }}>
        <input
          type="text"
          className="input font-mono"
          placeholder="Promo code (e.g. SUMMER30)"
          value={promoInput}
          onChange={(e) => setPromoInput(e.target.value)}
          style={{ fontSize: 11, padding: '5px 8px' }}
        />
        <button type="submit" className="btn btn-sm" style={{ padding: '5px 10px' }}>
          <Tag size={11} /> Apply
        </button>
      </form>

      {/* Applied Discount Chips */}
      {appliedDiscounts.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {appliedDiscounts.map((d, idx) => (
            <span
              key={`${d}-${idx}`}
              className="badge badge-purple"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10, padding: '2px 6px' }}
            >
              {d}
              <button
                type="button"
                onClick={() => targetAppStore.removeDiscount(d)}
                style={{ background: 'none', border: 'none', color: 'var(--purple)', cursor: 'pointer', fontSize: 11 }}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Summary Rows */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
          padding: '8px 0',
          borderTop: '1px solid var(--border)',
          borderBottom: '1px solid var(--border)',
          fontSize: 12,
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--text-2)' }}>
          <span>Subtotal</span>
          <span style={{ fontFamily: 'var(--font-mono)' }}>${subtotal.toFixed(2)}</span>
        </div>

        {appliedDiscounts.length > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--purple)' }}>
            <span>Discount</span>
            <span style={{ fontFamily: 'var(--font-mono)' }}>
              {isNanDetected ? 'NaN' : `-$${discountAmount.toFixed(2)}`}
            </span>
          </div>
        )}

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: 14,
            fontWeight: 700,
            marginTop: 4,
          }}
        >
          <span style={{ color: 'var(--text-1)' }}>Order Total:</span>
          <span
            className={`price-tag ${isNanDetected || isNegativeDetected ? 'error' : ''}`}
            style={{
              fontSize: 16,
              color: isNanDetected ? 'var(--red)' : isNegativeDetected ? 'var(--red)' : 'var(--green)',
            }}
          >
            {displayTotal}
          </span>
        </div>
      </div>

      {/* Visual Error Callout when Bug is Triggered */}
      {isNanDetected && (
        <div
          className="animate-fade-in"
          style={{
            background: 'rgba(239, 68, 68, 0.15)',
            border: '2px solid var(--red)',
            borderRadius: 'var(--radius-md)',
            padding: '10px 12px',
            color: '#fff',
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
            boxShadow: '0 0 16px rgba(239, 68, 68, 0.4)',
            animation: 'pulse 1.5s infinite',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <AlertTriangle size={15} style={{ color: 'var(--red)' }} />
            <strong style={{ fontSize: 12, color: '#fca5a5', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              CRITICAL: Stripe Checkout Blocked ($NaN)
            </strong>
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-2)', lineHeight: 1.3 }}>
            Promotional item ($0) caused division by zero in discount formula.
          </div>
          <div style={{ fontSize: 10, color: 'var(--accent)', fontFamily: 'var(--font-mono)', marginTop: 2 }}>
            ⚡ WebMCP Agent capturing runtime stack dump...
          </div>
        </div>
      )}

      {isNegativeDetected && (
        <div
          className="animate-fade-in"
          style={{
            background: 'rgba(245, 158, 11, 0.15)',
            border: '2px solid var(--amber)',
            borderRadius: 'var(--radius-md)',
            padding: '10px 12px',
            color: '#fff',
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
            boxShadow: '0 0 16px rgba(245, 158, 11, 0.4)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <AlertTriangle size={15} style={{ color: 'var(--amber)' }} />
            <strong style={{ fontSize: 12, color: '#fcd34d', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              EXPLOIT DETECTED: Negative Transaction ($0 - $14.90)
            </strong>
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-2)', lineHeight: 1.3 }}>
            Unbounded voucher stacking allowed 110% discount. Store owes money.
          </div>
          <div style={{ fontSize: 10, color: 'var(--accent)', fontFamily: 'var(--font-mono)', marginTop: 2 }}>
            ⚡ WebMCP Agent inspecting state invariants...
          </div>
        </div>
      )}

      {paymentSuccess && (
        <div
          style={{
            background: 'var(--green-soft)',
            border: '1px solid var(--green)',
            borderRadius: 'var(--radius-sm)',
            padding: '8px 10px',
            fontSize: 11,
            color: 'var(--green)',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <CheckCircle2 size={14} />
          <span>Payment Authorized! Order confirmed and processed.</span>
        </div>
      )}

      {/* Action Button */}
      <button
        className={`btn ${isNanDetected || isNegativeDetected ? 'btn-red' : 'btn-green'}`}
        onClick={handleCheckout}
        disabled={isProcessing || cart.length === 0}
        style={{
          width: '100%',
          padding: '10px',
          fontWeight: 600,
          fontSize: 13,
          boxShadow: isNanDetected ? 'var(--glow-red)' : 'var(--shadow-sm)',
        }}
      >
        {isProcessing
          ? 'Processing Payment...'
          : isNanDetected
          ? 'Pay $NaN (Triggers Crash)'
          : isNegativeDetected
          ? `Pay -$${Math.abs(rawTotal).toFixed(2)} (Fraud Check)`
          : `Authorize & Pay $${rawTotal.toFixed(2)}`}
      </button>
    </div>
  );
};
